import React, { useState, useRef, useCallback, useEffect } from "react";
import { CallClient } from "@configural/runtime-client/call-client";
import { decodeGeminiServerContent } from "@configural/runtime-client/gemini-live";
import { AudioPlayer } from "../audio/playback.js";
import { useHierarchy } from "../contexts/HierarchyContext.jsx";

// Fallback WS URL if sub-tenant has no sandyWsUrl configured
const SANDY_WS_FALLBACK = `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/sandy-ws`;

function formatTime() {
  return new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

export default function ManualCall({ navigate }) {
  const { subTenantId: tenantId, currentSubTenant } = useHierarchy();
  // ManualCall connects to Sandy via the sub-tenant's configured WS URL
  // (falls back to the Vite proxy /sandy-ws if not configured)
  const [status, setStatus] = useState("disconnected");
  const [transcript, setTranscript] = useState([]);
  const [toolCalls, setToolCalls] = useState([]);
  const [activeAgent, setActiveAgent] = useState("orchestrator");
  const [taskQueue, setTaskQueue] = useState([]);
  const [expandedTool, setExpandedTool] = useState(null);

  const clientRef = useRef(null);
  const audioCtxRef = useRef(null);
  const workletRef = useRef(null);
  const streamRef = useRef(null);
  const playerRef = useRef(new AudioPlayer());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  const addTranscript = useCallback((role, text, agent) => {
    if (!text) return;
    setTranscript((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === role && last.type === "message") {
        return [...prev.slice(0, -1), { ...last, text: last.text + text }];
      }
      return [...prev, { type: "message", role, text, agent, time: formatTime() }];
    });
  }, []);

  // Decode Gemini-Live `serverContent` via the SDK helper, then wire to
  // audio playback + transcript state.
  const handleRaw = useCallback((msg) => {
    const sc = decodeGeminiServerContent(msg);
    if (!sc) return;
    for (const audio of sc.audioParts) playerRef.current.play(audio);
    if (sc.inputText) addTranscript("patient", sc.inputText, activeAgent);
    if (sc.outputText) addTranscript("sandy", sc.outputText, activeAgent);
    if (sc.turnComplete) setTranscript((prev) => [...prev, { type: "divider" }]);
  }, [addTranscript, activeAgent]);

  const startMic = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 16000 },
    });
    streamRef.current = stream;
    const audioCtx = new AudioContext({ sampleRate: 16000 });
    audioCtxRef.current = audioCtx;
    await audioCtx.audioWorklet.addModule("/pcm-processor.js");
    const source = audioCtx.createMediaStreamSource(stream);
    const worklet = new AudioWorkletNode(audioCtx, "pcm-processor");
    workletRef.current = worklet;
    worklet.port.onmessage = (e) => {
      clientRef.current?.sendAudio(e.data);
    };
    source.connect(worklet);
  }, []);

  const disconnect = useCallback(() => {
    clientRef.current?.close(); clientRef.current = null;
    audioCtxRef.current?.close(); audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null;
    playerRef.current.stop(); playerRef.current = new AudioPlayer();
    setStatus("disconnected");
  }, []);

  const connect = useCallback(async () => {
    setStatus("connecting");
    setTranscript([]);
    setToolCalls([]);
    setActiveAgent("orchestrator");
    setTaskQueue([]);

    const wsUrl = currentSubTenant?.sandyWsUrl || SANDY_WS_FALLBACK;
    const sc = currentSubTenant?.sandyConfig || {};
    const configTenantId = sc.sandyTenantId || tenantId;
    const configSubTenantId = sc.sandySubTenantId || currentSubTenant?.slug || "";

    const client = new CallClient({
      wsUrl,
      tenantId: configTenantId,
      subTenantId: configSubTenantId || configTenantId,
      configMode: "draft",
      callMode: "voice",
      // SDK auto-sends this client_content envelope on `ready` so Sandy
      // speaks first.
      voiceKickoffText: "[Patient has connected to the call. Begin with your greeting and identity verification.]",
    });
    clientRef.current = client;

    client.on("ready", () => setStatus("connected"));

    client.on("toolCall", (e) => {
      const ctx = e.result?._agent_context;
      if (ctx) {
        setActiveAgent(ctx.active_agent);
        setTaskQueue(ctx.tasks || []);
      }
      setToolCalls((prev) => [...prev, { name: e.name, args: e.args, result: e.result, time: formatTime(), agent: activeAgent }]);
      if (e.name === "route_to_agent" && e.result?.routed) {
        setTranscript((prev) => [...prev, { type: "agent_switch", agent: e.result.agent, task: e.result.task?.description }]);
      }
      if (e.name === "complete_task") {
        setTranscript((prev) => [...prev, { type: "agent_switch", agent: e.result?.next_agent || "orchestrator", task: e.result?.all_tasks_done ? "All tasks completed" : e.result?.next_task?.description }]);
      }
      setTranscript((prev) => [...prev, { type: "tool_call", name: e.name, args: e.args, result: e.result, time: formatTime(), agent: activeAgent }]);
      if (e.name === "end_call") setTimeout(() => disconnect(), 3000);
    });

    client.on("raw", handleRaw);
    client.on("close", () => setStatus("disconnected"));

    setStatus("waiting");
    try {
      await client.connect();
      await startMic();
    } catch { setStatus("error"); }
  }, [handleRaw, startMic, tenantId, currentSubTenant, activeAgent, disconnect]);

  useEffect(() => () => disconnect(), [disconnect]);

  const isLive = status === "connected";
  const isOff = status === "disconnected" || status === "error";

  const AGENT_COLORS = {
    orchestrator: "#007a5a",
    scheduling: "#1264A3",
    "appointment-prep": "#E65100",
    triage: "#C62828",
  };
  const AGENT_NAMES = {
    orchestrator: "Sandy (Coordinator)",
    scheduling: "Scheduling Agent",
    "appointment-prep": "Appointment Prep Agent",
    triage: "Triage Agent",
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Manual Voice Call</h1>
          <p style={s.subtitle}>Speak directly to Sandy through your microphone</p>
        </div>
        <div style={s.headerRight}>
          {isLive && (
            <div style={{ ...s.agentBadge, background: AGENT_COLORS[activeAgent] || "#666" }}>
              {AGENT_NAMES[activeAgent] || activeAgent}
            </div>
          )}
          {isOff && transcript.length > 0 && (
            <button style={s.dlBtn} onClick={() => {
              const lines = [`Manual Call Transcript`, `Tenant: ${tenantId}`, `Date: ${new Date().toLocaleString()}`, "=".repeat(60), ""];
              for (const e of transcript) {
                if (e.type === "message") { lines.push(`[${e.time}] ${e.role === "patient" ? "Patient" : `Sandy (${e.agent || "orchestrator"})`}: ${e.text}`); }
                else if (e.type === "tool_call") { lines.push(`[${e.time}] TOOL: ${e.name}(${JSON.stringify(e.args)})`); }
                else if (e.type === "agent_switch") { lines.push(`--- Agent: ${e.agent} ---`); }
              }
              const blob = new Blob([lines.join("\n")], { type: "text/plain" });
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
              a.download = `call-${new Date().toISOString().slice(0, 10)}-transcript.txt`; a.click();
            }}>Download Transcript</button>
          )}
          {isOff ? (
            <button style={s.startBtn} onClick={connect}>Start Call</button>
          ) : (
            <button style={s.endBtn} onClick={disconnect}>End Call</button>
          )}
        </div>
      </div>

      <div style={s.body}>
        {/* Main transcript area */}
        <div style={s.transcriptPanel}>
          {/* Task queue */}
          {isLive && taskQueue.length > 0 && (
            <div style={s.taskBar}>
              {taskQueue.map((t) => (
                <div key={t.id} style={{ ...s.taskPill, background: t.status === "completed" ? "#e8f5e9" : t.status === "in_progress" ? "#fff3e0" : "#f5f5f5", color: t.status === "completed" ? "#2e7d32" : t.status === "in_progress" ? "#e65100" : "#666" }}>
                  {t.status === "completed" ? "\u2713" : t.status === "in_progress" ? "\u25B6" : "\u25CB"} {t.description}
                </div>
              ))}
            </div>
          )}

          {/* Transcript */}
          <div style={s.transcript}>
            {transcript.length === 0 && (
              <div style={s.empty}>
                <div style={s.emptyIcon}>&#9742;</div>
                <h3 style={s.emptyTitle}>Ready to call Sandy</h3>
                <p style={s.emptyText}>Click "Start Call" and speak into your microphone. Sandy will verify your identity first, then help with scheduling, triage, or appointment preparation.</p>
              </div>
            )}

            {transcript.map((entry, i) => {
              if (entry.type === "divider") {
                return <div key={i} style={s.divider} />;
              }

              if (entry.type === "agent_switch") {
                const color = AGENT_COLORS[entry.agent] || "#666";
                return (
                  <div key={i} style={s.agentSwitch}>
                    <div style={s.agentSwitchLine} />
                    <div style={{ ...s.agentSwitchBadge, background: color }}>{(AGENT_NAMES[entry.agent] || entry.agent)[0]}</div>
                    <span style={s.agentSwitchText}><strong>{AGENT_NAMES[entry.agent] || entry.agent}</strong>{entry.task && ` \u2014 ${entry.task}`}</span>
                    <div style={s.agentSwitchLine} />
                  </div>
                );
              }

              if (entry.type === "tool_call") {
                const isExpanded = expandedTool === i;
                const color = AGENT_COLORS[entry.agent] || "#666";
                return (
                  <div key={i} style={s.toolCard}>
                    <div style={s.toolHeader} onClick={() => setExpandedTool(isExpanded ? null : i)}>
                      <div style={{ ...s.toolDot, background: color }} />
                      <span style={{ ...s.toolName, color }}>{entry.name}</span>
                      <span style={s.toolAgent}>{AGENT_NAMES[entry.agent] || entry.agent}</span>
                      <span style={s.toolTime}>{entry.time}</span>
                      <span style={s.toolChevron}>{isExpanded ? "\u25B2" : "\u25BC"}</span>
                    </div>
                    {isExpanded && (
                      <div style={s.toolBody}>
                        <div style={s.toolSection}>
                          <div style={s.toolLabel}>INPUT</div>
                          <pre style={s.toolPre}>{JSON.stringify(entry.args, null, 2)}</pre>
                        </div>
                        {entry.result && (
                          <div style={s.toolSection}>
                            <div style={{ ...s.toolLabel, color: "#2e7d32" }}>OUTPUT</div>
                            <pre style={{ ...s.toolPre, background: "#f0faf0" }}>{JSON.stringify(entry.result, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (entry.type === "message") {
                const isPatient = entry.role === "patient";
                const color = isPatient ? "#1264A3" : (AGENT_COLORS[entry.agent] || "#007a5a");
                const name = isPatient ? "You (Patient)" : (AGENT_NAMES[entry.agent] || "Sandy");
                return (
                  <div key={i} style={s.msgRow}>
                    <div style={{ ...s.avatar, background: color }}>{name[0]}</div>
                    <div style={s.msgBody}>
                      <div style={s.msgMeta}>
                        <span style={s.msgName}>{name}</span>
                        <span style={s.msgTime}>{entry.time}</span>
                      </div>
                      <div style={s.msgText}>{entry.text}</div>
                    </div>
                  </div>
                );
              }

              return null;
            })}

            {isLive && transcript.length > 0 && (
              <div style={s.listening}>
                <span style={s.listeningDot} /> Listening...
              </div>
            )}
            <div ref={scrollRef} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = {
  page: { height: "100vh", display: "flex", flexDirection: "column" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 28px", borderBottom: "1px solid #eee" },
  title: { fontSize: 20, fontWeight: 600, color: "#0f0f0f", margin: 0 },
  subtitle: { fontSize: 13, color: "#888", marginTop: 2 },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
  agentBadge: { padding: "5px 12px", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 600 },
  dlBtn: { padding: "6px 14px", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 6, color: "#333", fontSize: 12, fontWeight: 500, cursor: "pointer" },
  startBtn: { padding: "8px 20px", background: "#0f0f0f", border: "none", borderRadius: 6, color: "#fff", fontSize: 14, fontWeight: 500 },
  endBtn: { padding: "8px 20px", background: "#dc2626", border: "none", borderRadius: 6, color: "#fff", fontSize: 14, fontWeight: 500 },

  body: { flex: 1, overflow: "hidden", display: "flex" },
  transcriptPanel: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },

  taskBar: { display: "flex", gap: 6, padding: "8px 20px", borderBottom: "1px solid #f0f0f0", flexWrap: "wrap", background: "#fafafa" },
  taskPill: { padding: "3px 10px", borderRadius: 4, fontSize: 12, fontWeight: 500 },

  transcript: { flex: 1, overflowY: "auto", padding: "16px 20px" },
  empty: { textAlign: "center", padding: "80px 20px" },
  emptyIcon: { fontSize: 40, marginBottom: 12 },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: "#0f0f0f", margin: "0 0 6px" },
  emptyText: { fontSize: 14, color: "#888", maxWidth: 400, margin: "0 auto", lineHeight: 1.5 },

  divider: { height: 1, background: "#f0f0f0", margin: "8px 0" },

  agentSwitch: { display: "flex", alignItems: "center", gap: 8, margin: "12px 0" },
  agentSwitchLine: { flex: 1, height: 1, background: "#e0e0e0" },
  agentSwitchBadge: { width: 24, height: 24, borderRadius: 6, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 },
  agentSwitchText: { fontSize: 12, color: "#555", whiteSpace: "nowrap" },

  toolCard: { background: "#fafafa", border: "1px solid #eee", borderRadius: 8, marginBottom: 6, overflow: "hidden" },
  toolHeader: { display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", cursor: "pointer", fontSize: 13 },
  toolDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  toolName: { fontWeight: 600, fontSize: 12 },
  toolAgent: { fontSize: 11, color: "#999", flex: 1 },
  toolTime: { fontSize: 11, color: "#bbb" },
  toolChevron: { fontSize: 9, color: "#bbb" },
  toolBody: { padding: "0 10px 10px", display: "flex", gap: 8, flexWrap: "wrap" },
  toolSection: { flex: 1, minWidth: 200 },
  toolLabel: { fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 },
  toolPre: { fontSize: 11, color: "#444", background: "#f5f5f5", border: "1px solid #eee", borderRadius: 4, padding: "6px 8px", margin: 0, fontFamily: "monospace", whiteSpace: "pre-wrap", lineHeight: 1.4, maxHeight: 150, overflowY: "auto" },

  msgRow: { display: "flex", gap: 10, marginBottom: 6, alignItems: "flex-start" },
  avatar: { width: 28, height: 28, borderRadius: 6, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 },
  msgBody: { flex: 1, minWidth: 0 },
  msgMeta: { display: "flex", gap: 6, alignItems: "baseline", marginBottom: 1 },
  msgName: { fontSize: 13, fontWeight: 600, color: "#0f0f0f" },
  msgTime: { fontSize: 11, color: "#bbb" },
  msgText: { fontSize: 14, color: "#333", lineHeight: 1.5, wordBreak: "break-word" },

  listening: { display: "flex", alignItems: "center", gap: 6, padding: "8px 0 4px 38px", fontSize: 12, color: "#999", fontStyle: "italic" },
  listeningDot: { width: 6, height: 6, borderRadius: "50%", background: "#007a5a", animation: "pulse 1.5s infinite" },
};
